/**
 * Onboarding v2 — AI demo prompt matrix (step #10).
 * Spec: specs/onboarding-rebuild.md
 *
 * Resolves a (primary_goal, current_struggles) pair to the user-message
 * Grace responds to in the live AI demo at step #10. Lookup walks:
 *   1. Pick the user's top struggle by STRUGGLE_PRIORITY hierarchy
 *   2. Try SPECIFIC_PROMPTS[goal][struggle] — high-leverage combos
 *   3. Fall back to STRUGGLE_FALLBACKS[struggle] — works across goals
 *   4. (Edge) If no struggle resolves, fall back to GENERIC_FALLBACK
 *
 * Voice: first-person raw vulnerability (Mau-style honesty), 25-50
 * words, designed to elicit Scripture grounding + practical tactic.
 *
 * To audit Grace's actual response quality on these prompts, run:
 *   node scripts/test-grace-prompts.mjs
 * (requires ANTHROPIC_API_KEY in env). Results land in briefs/.
 */

/**
 * Struggle hierarchy (top wins on tie). Locked 2026-05-03.
 * Logic: weight by (a) signal strength when picked,
 *                  (b) Illuminate-specific demo leverage.
 */
export const STRUGGLE_PRIORITY = [
  'doubt', // heaviest weight — admitted = dominating headspace
  'defending_faith', // specific, Illuminate's wheelhouse
  'sermon_prep', // niche but high-intent — pastors don't pick casually
  'scripture_comprehension', // bread-and-butter
  'prayer_life', // universal, emotional
  'daily_walk', // broad
  'staying_consistent', // broadest, fallback
]

/**
 * Pick the highest-priority struggle from the user's multi-select.
 * Returns null if the array is empty.
 */
export function pickTopStruggle(currentStruggles = []) {
  if (!Array.isArray(currentStruggles) || currentStruggles.length === 0) return null
  for (const struggle of STRUGGLE_PRIORITY) {
    if (currentStruggles.includes(struggle)) return struggle
  }
  // Selection contains an unknown struggle — return first as best-effort.
  return currentStruggles[0]
}

// ── Specific cells (5) — high-volume or high-leverage combos. ──
export const SPECIFIC_PROMPTS = {
  defend_faith: {
    doubt:
      "I want to be able to defend my faith but the honest truth is I doubt some of it myself. How do I get to a place where I'm actually convinced — convinced enough to speak up?",
  },
  explore_christianity: {
    doubt:
      "I'm exploring Christianity but every time I get close, doubts pull me back. Is it okay to be this skeptical and still want to believe? Where do I even start?",
    scripture_comprehension:
      "I'm new to Christianity and the Bible feels overwhelming — it's huge, it's old, it's strange in places. Where would you start me, and how do I read it without missing what actually matters?",
  },
  sermon_prep: {
    // Encouragement angle (not workflow) — pastors might pick sermon_prep
    // as both goal and struggle without being "stuck"; they just teach. The
    // depletion frame lands universally and avoids the workflow ask-back trap.
    sermon_prep:
      "I preach every week and honestly I feel like I'm pouring out more than I'm taking in. How do I keep my own faith alive while feeding everyone else's?",
  },
  deepen_faith: {
    staying_consistent:
      'I want to grow, I really do — but I keep falling off. Strong week, then nothing for a month, then guilt, then start over. How do I actually break this cycle?',
  },
}

// ── Struggle fallbacks (7) — used when no specific cell hits. ──
export const STRUGGLE_FALLBACKS = {
  doubt:
    "I'm wrestling with doubt I can't shake — like the foundation I built my faith on isn't quite holding. How do I move forward when even the basics feel unsteady?",
  defending_faith:
    'Someone keeps pushing back on what I believe and I freeze every time. Help me get sharper so I can respond with conviction instead of retreating.',
  // Encouragement angle (isolation frame). Avoids workflow ask-back; gives
  // Grace a complete pastoral moment to respond to in one shot.
  sermon_prep:
    "Most of my message prep happens late, alone, and some weeks it feels like nobody sees the cost of what I'm doing. How do I stay grounded in this work?",
  scripture_comprehension:
    "I open the Bible and a lot of it goes right over my head. How do I actually start understanding what I'm reading instead of just bouncing off the hard parts?",
  prayer_life:
    "My prayer life is honestly dead. Most days it feels like I'm talking to a wall, and I don't know how to make it real again. Where do I even start?",
  daily_walk:
    'My faith feels like something I do on Sundays, not something I live. What does it actually look like to walk with God on a Tuesday afternoon?',
  staying_consistent:
    'I keep falling off. Strong week, then nothing for a month, then guilt, then start over. How do I break this cycle and actually stay in it?',
}

// Last-resort fallback — should rarely fire (struggles is REQUIRED at step #9).
export const GENERIC_FALLBACK =
  "I want to grow in my faith but I'm not sure where to start. Where would you begin with someone like me?"

/**
 * Main lookup. Returns { prompt, source } where source ∈
 * 'specific' | 'fallback' | 'generic' for telemetry / Sentry tagging.
 */
export function getDemoPrompt(primaryGoal, currentStruggles = []) {
  const struggle = pickTopStruggle(currentStruggles)

  if (struggle && SPECIFIC_PROMPTS[primaryGoal]?.[struggle]) {
    return { prompt: SPECIFIC_PROMPTS[primaryGoal][struggle], source: 'specific' }
  }
  if (struggle && STRUGGLE_FALLBACKS[struggle]) {
    return { prompt: STRUGGLE_FALLBACKS[struggle], source: 'fallback' }
  }
  return { prompt: GENERIC_FALLBACK, source: 'generic' }
}

/**
 * Test scaffolding — every (goal, struggle) test combo we want to send
 * through Grace, with a label and the prompt that would fire.
 * Used by scripts/test-grace-prompts.mjs.
 */
export const TEST_COMBOS = [
  // 5 specific cells
  { label: 'defend_faith × doubt (specific)', goal: 'defend_faith', struggle: 'doubt' },
  {
    label: 'explore_christianity × doubt (specific)',
    goal: 'explore_christianity',
    struggle: 'doubt',
  },
  {
    label: 'explore_christianity × scripture_comp (specific)',
    goal: 'explore_christianity',
    struggle: 'scripture_comprehension',
  },
  { label: 'sermon_prep × sermon_prep (specific)', goal: 'sermon_prep', struggle: 'sermon_prep' },
  {
    label: 'deepen_faith × staying_consistent (specific)',
    goal: 'deepen_faith',
    struggle: 'staying_consistent',
  },
  // 7 struggle fallbacks (each tested via a representative goal that has no specific cell)
  { label: '× doubt (fallback) — via deepen_faith', goal: 'deepen_faith', struggle: 'doubt' },
  {
    label: '× defending_faith (fallback) — via deepen_faith',
    goal: 'deepen_faith',
    struggle: 'defending_faith',
  },
  {
    label: '× sermon_prep (fallback) — via deepen_faith',
    goal: 'deepen_faith',
    struggle: 'sermon_prep',
  },
  {
    label: '× scripture_comprehension (fallback) — via understand_bible',
    goal: 'understand_bible',
    struggle: 'scripture_comprehension',
  },
  {
    label: '× prayer_life (fallback) — via deepen_faith',
    goal: 'deepen_faith',
    struggle: 'prayer_life',
  },
  {
    label: '× daily_walk (fallback) — via deepen_faith',
    goal: 'deepen_faith',
    struggle: 'daily_walk',
  },
  {
    label: '× staying_consistent (fallback) — via understand_bible',
    goal: 'understand_bible',
    struggle: 'staying_consistent',
  },
]
